import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { formatAmount } from '../../../utils/appointments/formatAmount';

import {
  URGENT_VISIT_TYPE_CODE,
  APPOINTMENT_STATUSES,
  PAYMENT_STATUSES,
  INLINE_TYPES,
} from '../../../constants';
import {
  formatHours,
  formatToCalendarTime,
  momentFormat,
} from '../../../utils/appointments/dateFilter';

const tScope = 'healthboard.appointments.paymentStatus';

export function formatPaymentInfo(
  { paymentStatus = '', status = {}, paidAmount, copay: price, visitType },
  detailsView = false,
) {
  const appointmentStatus = get(status, 'coreAppName');
  const isUrgentVisit = get(visitType, 'code') === URGENT_VISIT_TYPE_CODE;

  switch (paymentStatus) {
    case PAYMENT_STATUSES.PAID:
      return {
        id: `${tScope}.paidAmount`,
        scope: { paidAmount: formatAmount(paidAmount || 0) },
      };

    case PAYMENT_STATUSES.VOIDED:
      return {
        id: `${tScope}.refunded`,
        ...(isUrgentVisit && !price && { id: `${tScope}.tapToPay` }),
        scope: {
          price: formatAmount(price || 0),
          paidAmount: formatAmount(paidAmount || 0),
        },
      };

    case PAYMENT_STATUSES.PENDING:
      return { id: `${tScope}.processingPayment` };

    case PAYMENT_STATUSES.UNPAID:
    case PAYMENT_STATUSES.FAILED:
      switch (appointmentStatus) {
        case APPOINTMENT_STATUSES.DRAFT:
        case APPOINTMENT_STATUSES.WAITING:
        case APPOINTMENT_STATUSES.UPCOMING:
        case APPOINTMENT_STATUSES.IN_PROCESS:
        case APPOINTMENT_STATUSES.CHECKED_IN: {
          const payI18n = detailsView ? 'totalToPay' : 'tapToPay';
          const isZeroCopay = price === 0;

          return price || isZeroCopay
            ? {
                id: `${tScope}.${payI18n}`,
                scope: { price: formatAmount(isZeroCopay ? 0 : price) },
                canPay: true,
              }
            : { id: `${tScope}.amountPending` };
        }

        case APPOINTMENT_STATUSES.NO_SHOW:
        case APPOINTMENT_STATUSES.CANCELED:
        case APPOINTMENT_STATUSES.COMPLETED: {
          return {
            id: `${tScope}.amountPending`,
            ...(isUrgentVisit && {
              canPay: true,
              scope: { price: formatAmount(price || 0) },
            }),
          };
        }

        default:
          return { id: `${tScope}.amountPending` };
      }

    default:
      return { id: `${tScope}.amountPending` };
  }
}

export function formatPaymentInfoProviderContext({
  paymentStatus = '',
  paidAmount,
  copay: price,
}) {
  switch (paymentStatus) {
    case PAYMENT_STATUSES.PAID:
      return {
        id: `${tScope}.paidAmount`,
        scope: { paidAmount: formatAmount(paidAmount || 0) },
      };

    case PAYMENT_STATUSES.VOIDED:
      return {
        id: `${tScope}.voided`,
        scope: { paidAmount: formatAmount(paidAmount || 0) },
      };

    case PAYMENT_STATUSES.FAILED:
    case PAYMENT_STATUSES.UNPAID:
      return {
        id: `${tScope}.unpaidAmount`,
        scope: { price: formatAmount(price || 0) },
      };

    default:
      return { id: `${tScope}.amountPending` };
  }
}

export function visitOverviewAdapter({
  providerInfo,
  dateTime,
  location,
  note,
  reasonType,
} = {}) {
  const provider = {
    ...providerInfo,
    location,
  };
  const schedule = { time: dateTime };
  const reason = {
    type: get(reasonType, 'name'),
    note: note || '',
  };

  return { provider, schedule, reason };
}

export function appointmentTargetUserAdapter(appointment, providerView) {
  let provider = get(appointment, 'providerInfo');
  const location = get(appointment, 'location');

  if (providerView) {
    const username = get(appointment, 'patientUserInfo.username');
    const name = get(appointment, 'patientUserInfo.displayableName', username);
    const avatar = get(appointment, 'patientUserInfo.avatar');

    provider = { name, avatar };
  }

  return {
    provider: { ...provider, location },
  };
}
const onlyDateFormat = momentFormat('MMM D');

export function getAppointmentTime({ appointment, isUpcoming }) {
  const dateTime = get(appointment, 'dateTime');
  return {
    hours: formatHours(dateTime),
    date: isUpcoming
      ? formatToCalendarTime(dateTime)
      : onlyDateFormat(dateTime),
  };
}

export function getPaymentDetails({
  appointment,
  providerContext,
  formatMessage,
}) {
  const status = get(appointment, 'status.coreAppName');
  const code = get(appointment, 'visitType.code');

  if (INLINE_TYPES.includes(code)) {
    return status;
  }

  const { id, scope } = providerContext
    ? formatPaymentInfoProviderContext(appointment)
    : formatPaymentInfo(appointment);
  const translation = formatMessage({ id }, scope);
  return `${translation}`;
}

export function getInlineCellData(appointment, formatMessage) {
  const startTime = get(
    appointment,
    'location.schedule.metadata.startTime',
    '',
  );
  const endTime = get(appointment, 'location.schedule.metadata.endTime', '');

  return {
    title: formatMessage({ id: 'common.hours' }),
    text: `${formatHours(startTime)} - ${formatHours(endTime)}`,
  };
}

export function getTransactionInfo(formatMessage, appointment) {
  if (isEmpty(appointment)) return null;
  const externalTransaction = get(appointment, 'externalTransaction', {});
  const { id, createdAt } = externalTransaction || {};

  return [
    [
      {
        title: formatMessage({ id: 'appointment.info.transactionDate' }),
        subtitle: createdAt ? momentFormat('L')(createdAt) : ' - ',
      },
      {
        title: formatMessage({ id: 'appointment.info.transactionId' }),
        subtitle: id || ' - ',
      },
    ],
  ];
}

export function checkMandatoryPaymentWarning(appointment) {
  if (!appointment) return false;
  const { isRequiredPayment, copay, paymentStatus } = appointment;
  if (!isRequiredPayment) return false;
  return copay > 0 && paymentStatus !== PAYMENT_STATUSES.PAID;
}

export function getMyProviderDetails(formatMessage, provider, languageTypes) {
  if (isEmpty(provider)) return null;

  const getLanguageString = languages => {
    return languages
      .map(key => {
        const matched = languageTypes.find(({ value }) => key === value);
        return matched ? matched.label : key;
      })
      .join(', ');
  };

  return [
    [
      {
        title: formatMessage({ id: 'appointments.specialization' }),
        subtitle: provider.specialization || ' - ',
      },
      {
        title: formatMessage({ id: 'profile.fields.language' }),
        subtitle:
          provider.languages.length > 0
            ? getLanguageString(provider.languages)
            : ' - ',
      },
    ],
  ];
}



// WEBPACK FOOTER //
// ./src/components/Healthboard/utils/appointment.js